import React, { useState } from "react";
import moment from "moment";
import md5 from "md5";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { errorColor, bodyColor } from "../../../../config/constants";
import { parsedDate } from "../../../../config/functions";
// Components
import {
  PersonalInformationDiv,
  TitleTextDiv,
  DataDiv,
  ColumnDataDiv,
  InputDataDiv,
  Label,
  Input,
  RelativeButton,
  InputBirthday,
  Textarea,
} from "./styles";
import Select from "../../../selectInput";
// Assets
import SelectArrow from "../../../../assets/ico_arrow_down.svg";
import VisiblePasswordImg from "../../../../assets/btn_visible_password.svg";
import HidePasswordImg from "../../../../assets/btn_hide_password.svg";

const PersonalInfo = ({
  disabledInputs,
  user,
  password,
  setPassword,
  passwordError,
  setPasswordError,
  gender,
  setGender,
  genderError,
  setGenderError,
  height,
  setHeight,
  heightError,
  setHeightError,
  dateOfBirth,
  setDateOfBirth,
  dateOfBirthError,
  setDateOfBirthError,
  gameExperience,
  setGameExperience,
  computerExperience,
  setComputerExperience,
  otherInfo,
  setOtherInfo,
  pareticSide,
  setPareticSide,
  patientVersion,
  setPersonalInfo,
}) => {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [showButtonPassword, setShowButtonPassword] = useState(false);
  const [t] = useTranslation("global");

  const handleSetPasswordVisibility = (e) => {
    e.preventDefault();
    setVisiblePassword(!visiblePassword);
  };

  const handleSetInput = (e, setInput, setError, input) => {
    setInput(e.target.value);
    setError && setError(false);
    setPersonalInfo({
      PASSWORD: input === "password" ? (e.target.value !== "" ? md5(`${user}${e.target.value}`) : e.target.value) : password,
      GENDER: input === "gender" ? e.target.value : gender,
      PTN_HEIGHT_CM: input === "height" ? Number(e.target.value) : Number(height),
      BIRTH_DATE:
        input === "birthDate"
          ? moment(parsedDate(e.target.value), "DD/MM/YYYY").format("YYYY/MM/DD")
          : moment(parsedDate(dateOfBirth), "DD/MM/YYYY").format("YYYY/MM/DD"),
      VIDEOGAME_EXP: input === "videoGameExp" ? e.target.value : gameExperience,
      COMPUTER_EXP: input === "computerExp" ? e.target.value : computerExperience,
      COMMENTS: input === "comment" ? e.target.value : otherInfo,
      PARETIC_SIDE: input === "pareticSide" ? e.target.value : pareticSide,
      VERSION: patientVersion,
    });
  };

  const handleShowPassword = (e, setPassword, setPasswordError) => {
    handleSetInput(e, setPassword, setPasswordError, "password");
    if (e.target.value.length > 0) {
      setShowButtonPassword(true);
    } else {
      setShowButtonPassword(false);
    }
  };

  return (
    <PersonalInformationDiv>
      <TitleTextDiv>
        <p>{t("personalInformation.title")}</p>
      </TitleTextDiv>
      <DataDiv>
        <ColumnDataDiv>
          <InputDataDiv>
            <Label htmlFor="login" fontWeight="bold">
              {t("personalInformation.userNameLabel")} *
            </Label>
            <Input type="text" placeholder={t("personalInformation.userNameInput")} id="login" value={user} disabled={true} />
          </InputDataDiv>
          <InputDataDiv>
            <Label htmlFor="password" fontWeight="bold">
              {t("personalInformation.passwordLabel")} *
            </Label>
            <Input
              type={visiblePassword ? "text" : "password"}
              placeholder="*******"
              id="password"
              value={password}
              onChange={(e) => handleShowPassword(e, setPassword, setPasswordError)}
              error={passwordError}
              padding="5px 32px 5px 10px"
              disabled={disabledInputs}
            />
            {showButtonPassword && (
              <RelativeButton onClick={handleSetPasswordVisibility}>
                <img src={visiblePassword ? HidePasswordImg : VisiblePasswordImg} alt="icon-password-button" />
              </RelativeButton>
            )}
          </InputDataDiv>
          <InputDataDiv>
            <Label fontWeight="bold">{t("patientGender.label")} *</Label>
            <Select
              value={gender}
              onChange={(e) => handleSetInput(e, setGender, setGenderError, "gender")}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              error={genderError}
              disabled={disabledInputs}
            >
              <option value="MALE">{t("patientGender.male")}</option>
              <option value="FEMALE">{t("patientGender.female")}</option>
            </Select>
          </InputDataDiv>
          <InputDataDiv>
            <Label htmlFor="height" fontWeight="bold">
              {t("personalInformation.heightLabel")} *
            </Label>
            <Input
              type="number"
              placeholder="175"
              id="height"
              value={height}
              onChange={(e) => handleSetInput(e, setHeight, setHeightError, "height")}
              error={heightError}
              disabled={disabledInputs}
            />
          </InputDataDiv>
          <InputDataDiv>
            <Label htmlFor="dateOfBirth" fontWeight="bold">
              {t("personalInformation.birthdayLabel")} *
            </Label>
            <InputBirthday
              format="##/##/####"
              placeholder="dd/mm/yyyy"
              mask={["d", "d", "m", "m", "y", "y", "y", "y"]}
              value={dateOfBirth}
              onChange={(e) => handleSetInput(e, setDateOfBirth, setDateOfBirthError, "birthDate")}
              error={dateOfBirthError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
              disabled={disabledInputs}
            />
          </InputDataDiv>
          <InputDataDiv>
            <Label fontWeight="bold">{t("pathologyInformation.pareticSideLabel")} *</Label>
            <Select
              value={pareticSide}
              onChange={(e) => handleSetInput(e, setPareticSide, null, "pareticSide")}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              disabled={disabledInputs}
            >
              <option value="LEFT">{t("pathologyInformation.pareticSideSelect.left")}</option>
              <option value="RIGHT">{t("pathologyInformation.pareticSideSelect.right")}</option>
            </Select>
          </InputDataDiv>
        </ColumnDataDiv>
        <ColumnDataDiv>
          <InputDataDiv>
            <Label>{t("personalInformation.videoGameExperienceLabel")}</Label>
            <Select
              value={gameExperience}
              onChange={(e) => handleSetInput(e, setGameExperience, null, "videoGameExp")}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              disabled={disabledInputs}
            >
              <option value={""}>{t("personalInformation.no")}</option>
              <option value={"1"}>{t("personalInformation.yes")}</option>
            </Select>
          </InputDataDiv>
          <InputDataDiv>
            <Label>{t("personalInformation.computerExperienceLabel")}</Label>
            <Select
              value={computerExperience}
              onChange={(e) => handleSetInput(e, setComputerExperience, null, "computerExp")}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              disabled={disabledInputs}
            >
              <option value={""}>{t("personalInformation.no")}</option>
              <option value={"1"}>{t("personalInformation.yes")}</option>
            </Select>
          </InputDataDiv>
          <InputDataDiv>
            <Label htmlFor="comment">{t("personalInformation.otherInformationLabel")}</Label>
            <Textarea
              id="comment"
              value={otherInfo}
              onChange={(e) => handleSetInput(e, setOtherInfo, null, "comment")}
              disabled={disabledInputs}
            />
          </InputDataDiv>
        </ColumnDataDiv>
      </DataDiv>
    </PersonalInformationDiv>
  );
};

export default PersonalInfo;
